<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FieldsetContractRentalData',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    contract: {
      type: Object,
      required: true,
    },
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
    noLegend: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);
  const b_1040167445267876_dataSource = computed(() => ({
    name: 'suggest_rental',
  }));

  const $data = computed(() => ({
    ux: ux.value,
    b_1040167445267876_dataSource: b_1040167445267876_dataSource.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'ux' },
      { name: 'b_1040167445267876_dataSource', nodeId: '1040167445267876' },
    ],
    loaders: {},
    ux: {},
    dataRefs: { ux, b_1040167445267876_dataSource },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const rentalRequired = computed(() => {
    return (
      ['rental', 'rental_agreement', 'parking'].indexOf(
        props.contract.contract_type
      ) > -1
    );
  });

  watch(
    () => props.contract,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contract');
    }
  );
  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );
  watch(
    () => props.noLegend,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('noLegend');
    }
  );

  const act_1040167445267876_setData_c9c72ve14_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'contract.rental',
      value: null,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_1040167445267876_setData_sid08t8vn_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'contract.rental',
      value: $event.data.response.data,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_1040167445267876_request_y35qzladf_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'read_rental',
      parameters: {
        id: $event.data.value.id,
      },
      headers: {
        accept: 'application/vnd.itp.rentalparty+json',
      },
    };
  };

  const act_7315092382398562_reloadComponentData_x66jebu2a =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_hmobntvn8 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_x66jebu2a,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_hmobntvn8],
    event: 'reload',
  });
  const act_1040167445267876_setData_c9c72ve14 = new actionTypes.SetDataAction({
    actionArgs: act_1040167445267876_setData_c9c72ve14_getActionArgs,
  });
  const inst_evh_1040167445267876_optionDeselected_g6cqmeym2 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_setData_c9c72ve14,
      event: 'optionDeselected',
      displayName: 'setData',
    });
  const inst_evh_1040167445267876_optionDeselected =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1040167445267876_optionDeselected_g6cqmeym2],
      event: 'optionDeselected',
    });
  const act_1040167445267876_setData_sid08t8vn = new actionTypes.SetDataAction({
    actionArgs: act_1040167445267876_setData_sid08t8vn_getActionArgs,
  });
  const inst_evh_1040167445267876_success_tlcrdkiga =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_setData_sid08t8vn,
      event: 'success',
      displayName: 'setData',
    });
  const act_1040167445267876_request_y35qzladf = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_y35qzladf_getActionArgs,
    events: [inst_evh_1040167445267876_success_tlcrdkiga],
  });
  const inst_evh_1040167445267876_optionSelected_pri52nimc =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_y35qzladf,
      event: 'optionSelected',
      displayName: 'request',
    });
  const inst_evh_1040167445267876_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1040167445267876_optionSelected_pri52nimc],
      event: 'optionSelected',
    });

  const actions = {
    inst_evh_7315092382398562_reload_hmobntvn8,
    inst_evh_7315092382398562_reload,
    inst_evh_1040167445267876_optionDeselected_g6cqmeym2,
    inst_evh_1040167445267876_optionDeselected,
    inst_evh_1040167445267876_success_tlcrdkiga,
    inst_evh_1040167445267876_optionSelected_pri52nimc,
    inst_evh_1040167445267876_optionSelected,
  };

  const evh_7315092382398562_reload_hmobntvn8 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_hmobntvn8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_optionDeselected_g6cqmeym2 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_1040167445267876_optionDeselected_g6cqmeym2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_optionDeselected = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_optionDeselected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_tlcrdkiga = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_tlcrdkiga.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_optionSelected_pri52nimc = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_1040167445267876_optionSelected_pri52nimc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_optionSelected = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <legend v-if="!!noLegend">
      {{ pgettext('Contract', 'Rental') }}
    </legend>
    <ItpFormGroup
      name="rental"
      :label="pgettext('Rental', 'Rental')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
      :required="rentalRequired"
    >
      <ItpSelect2
        name="rental"
        :required="rentalRequired"
        v-model="contract.rental"
        :value="contract.rental"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (contract.rental =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        bind-object
        :dataSource="b_1040167445267876_dataSource"
        textField="number,name"
        clearable
        optionTemplate="<UcSuggestRentalOption :option=option></UcSuggestRentalOption>"
        use-option-template-for-selected-option
        @optionDeselected="evh_1040167445267876_optionDeselected($event, {})"
        @optionSelected="evh_1040167445267876_optionSelected($event, {})"
      >
      </ItpSelect2>
    </ItpFormGroup>
    <div v-if="contract.rental && contract.rental.id">
      <ItpFormGroup
        name="name"
        :label="pgettext('Rental', 'Name')"
        :label-cols-md="4"
        :content-cols-md="8"
        :labelAlign="labelAlign"
      >
        <ItpFormInput
          name="name"
          v-model="contract.rental.name"
          :value="contract.rental.name"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (contract.rental.name =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
        >
        </ItpFormInput>
      </ItpFormGroup>
      <ItpFormGroup
        name="name"
        :label="pgettext('Rental', 'Address')"
        :label-cols-md="4"
        :content-cols-md="8"
        :labelAlign="labelAlign"
      >
        <ItpFormInput
          name="name"
          v-model="contract.rental.address"
          :value="contract.rental.address"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (contract.rental.address =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
        >
        </ItpFormInput>
      </ItpFormGroup>
      <ItpFormGroup
        name="area"
        :label="pgettext('Rental', 'Area')"
        :label-cols-md="4"
        :content-cols-md="4"
        :labelAlign="labelAlign"
      >
        <ItpFormInput
          name="name"
          v-model="contract.rental.area"
          :value="contract.rental.area"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (contract.rental.area =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
        >
        </ItpFormInput>
      </ItpFormGroup>
      <ItpFormGroup
        name="lot_number"
        :label="pgettext('Rental', 'LOT number')"
        :label-cols-md="4"
        :content-cols-md="4"
        :labelAlign="labelAlign"
      >
        <ItpFormInput
          name="name"
          v-model="contract.rental.lot_number"
          :value="contract.rental.lot_number"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (contract.rental.lot_number =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
        >
        </ItpFormInput>
      </ItpFormGroup>
      <ItpFormGroup
        name="house_number"
        :label="pgettext('Rental', 'House number')"
        :label-cols-md="4"
        :content-cols-md="4"
        :labelAlign="labelAlign"
      >
        <ItpFormInput
          name="name"
          v-model="contract.rental.house_number"
          :value="contract.rental.house_number"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (contract.rental.house_number =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
        >
        </ItpFormInput>
      </ItpFormGroup>
      <ItpFormGroup
        name="description"
        :label="pgettext('Rental', 'Description')"
        :label-cols-md="4"
        :content-cols-md="8"
        :labelAlign="labelAlign"
      >
        <ItpFormTextarea
          name="name"
          v-model="contract.rental.description"
          :value="contract.rental.description"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (contract.rental.description =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
        >
        </ItpFormTextarea>
      </ItpFormGroup>
    </div>
  </ItpUsercontrol>
</template>
